import { useEffect, useState, useCallback } from 'react';

import { get, isEmpty } from 'ravenjs/utils/lodash';
import { callFunc } from 'ravenjs/utils/actions';

import { RESOURCES } from 'constants/tickets';
import REGEX_VALIDATE from 'constants/regex';
import { canEditPAResources } from 'utils/tickets';

export function usePreppedAnswerResources({
    createResources,
    currentUserId,
    deleteResources,
    fetchCaseResourceTypes,
    fetchPreppedAnswerDetails,
    fetchUsers,
    onSubmitted,
    preppedAnswerCurrentState,
    preppedAnswerId,
    showList,
    submitToBackEnd = true,
    ticketId,
    updateResources,
    useReadOnlyMode,
}) {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [body, setBody] = useState('');
    const [link, setLink] = useState('');
    const [resourceType, setResourceType] = useState();
    const [isLinkValid, setIsLinkValid] = useState(false);
    const [resources, setResources] = useState([]);
    const [resourceTypes, setResourceTypes] = useState([]);

    const getCaseResourceTypes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchCaseResourceTypes();

            const resourceTypes = response.reduce((selectableResourceTypes, option) => {
                if (get(option, 'attributes.user_selectable', false)) {
                    selectableResourceTypes.push({
                        value: option.id,
                        label: option.attributes.label,
                        description: option.attributes.description,
                    });
                }
                return selectableResourceTypes;
            }, []);

            setResourceTypes(resourceTypes);
            setResourceType(resourceTypes[0]);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [fetchCaseResourceTypes]);

    const getPreppedAnswerResources = useCallback(async () => {
        try {
            if (!isEmpty(preppedAnswerId)) {
                const params = {
                    include: ['resources'],
                };
                setLoading(true);
                const detailsResponse = await fetchPreppedAnswerDetails({
                    params,
                    id: preppedAnswerId,
                });

                const includedData = get(detailsResponse, 'included', {});
                let resources = [];
                const userIdSet = new Set();
                const usersMap = {};

                if (!isEmpty(includedData)) {
                    includedData.forEach(data => {
                        const { attributes, type, id } = data;
                        userIdSet.add(attributes.user_id);
                        if (type === 'external_resources') resources.push({ attributes, id });
                    });
                }

                if (userIdSet.size) {
                    const preppedAnswerAuthorId = get(
                        detailsResponse,
                        'data.attributes.user_id',
                        null
                    );
                    const usersResponse = await fetchUsers({ userId: [...userIdSet] });
                    const users = get(usersResponse, 'users', []);
                    users.forEach(user => (usersMap[user.userId] = user.fullName));
                    resources = resources.map(({ attributes, ...rest }) => {
                        return {
                            attributes: {
                                creatorFullName: usersMap[attributes.user_id],
                                preppedAnswerAuthorId,
                                ...attributes,
                            },
                            ...rest,
                        };
                    });
                }

                setResources(resources);
            }
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [fetchPreppedAnswerDetails, fetchUsers, preppedAnswerId]);

    const onSubmit = async () => {
        const requestPayload = {
            data: {
                type: 'external_resources',
                attributes: {
                    body,
                    link,
                    type: resourceType.value,
                },
            },
        };

        if (preppedAnswerId) {
            requestPayload.data.attributes.prepped_answer_id = preppedAnswerId;
        }

        if (ticketId) {
            requestPayload.data.attributes.ticket_id = ticketId;
        }

        try {
            setLoading(true);
            if (submitToBackEnd) {
                await createResources(requestPayload);
            }
            return Promise.resolve();
        } catch (e) {
            setError(RESOURCES.ERROR.CREATE);
            return Promise.reject(e);
        } finally {
            if (submitToBackEnd && showList) {
                getPreppedAnswerResources();
            }
            setBody('');
            setLink('');
            setIsLinkValid(false);
            setLoading(false);
            const resourceData = get(requestPayload, 'data');
            callFunc(onSubmitted, resourceData);
        }
    };

    const onClickUpdateResource = async (id, body, link, type) => {
        setLoading(true);
        const data = {
            data: {
                data: {
                    id,
                    attributes: { body, link, type },
                    type: 'external_resources',
                },
            },
        };
        await updateResources(data)
            .then(() => {
                setLoading(false);
                showList && getPreppedAnswerResources();
            })
            .catch(() => {
                setLoading(false);
                setError(RESOURCES.ERROR.UPDATE);
            });
    };

    const onClickResourceDelete = async id => {
        setLoading(true);
        await deleteResources({ id })
            .then(() => {
                showList && getPreppedAnswerResources();
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
                setError(err);
            });
    };

    const canDeleteResource = ({ creatorId }) => {
        let canDelete = false;
        if (
            canEditPAResources({
                currentUserId,
                currentState: preppedAnswerCurrentState,
                preppedAnswerAuthorId: creatorId,
            })
        ) {
            canDelete = true;
        }

        return canDelete && !useReadOnlyMode;
    };

    const canUpdateResource = ({ creatorId }) => {
        let canUpdate = false;
        if (
            canEditPAResources({
                currentUserId,
                currentState: preppedAnswerCurrentState,
                preppedAnswerAuthorId: creatorId,
            })
        ) {
            canUpdate = true;
        }

        return canUpdate && !useReadOnlyMode;
    };

    const onClickResourceLink = link => {
        if (!link?.match(/^(http|https)/g)) {
            window.open(`https://${link}`, '_blank', '');
        } else {
            window.open(link, '_blank', '');
        }
    };

    function onChangeBody(e) {
        setBody(e.target.value);
    }

    function onChangeLink(e) {
        setLink(e.target.value);
        setIsLinkValid(REGEX_VALIDATE.URL.test(e.target.value));
    }

    function onChangeType(option) {
        setResourceType(option);
        setLink('');
        setBody('');
    }

    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true);
                await getPreppedAnswerResources();
                await getCaseResourceTypes();
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [getPreppedAnswerResources, getCaseResourceTypes]);

    return {
        body,
        canDeleteResource,
        canUpdateResource,
        error,
        isLinkValid,
        link,
        loading,
        onChangeBody,
        onChangeLink,
        onChangeType,
        onClickResourceDelete,
        onClickResourceLink,
        onClickUpdateResource,
        onSubmit,
        resources,
        resourceType,
        resourceTypes,
    };
}
